import React, { useState } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  InputAdornment,
  Avatar,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  People,
  Business,
  Handshake,
  Task,
  Description,
  Settings,
  Logout,
  Search,
  AccountCircle,
  Lightbulb,
  TrendingUp,
  Email,
  Analytics,
  Assessment,
  HelpOutline,
  AutoAwesome,
  InsertDriveFile,
  MonetizationOn,
  PeopleAlt,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import FloatingChatBubble from './FloatingChatBubble';
import BusinessSuiteAppSwitcher from './BusinessSuiteAppSwitcher';

const drawerWidth = 260;

interface SidebarLayoutProps {
  children: React.ReactNode;
}

const SidebarLayout: React.FC<SidebarLayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  const isActive = (path: string) => location.pathname === path;

  const menuItems = [
    { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' },
    { text: 'Accounts', icon: <Business />, path: '/accounts' },
    { text: 'Contacts', icon: <People />, path: '/contacts' },
    { text: 'Deals', icon: <Handshake />, path: '/deals' },
    { text: 'Tasks', icon: <Task />, path: '/tasks' },
    { text: 'Documents', icon: <Description />, path: '/documents' },
    { text: 'Analytics', icon: <Analytics />, path: '/analytics' },
    { text: 'Executive Dashboard', icon: <Assessment />, path: '/executive' },
    { text: 'Performance', icon: <TrendingUp />, path: '/performance' },
    { text: 'Forecast', icon: <TrendingUp />, path: '/forecast' },
    { text: 'AI Insights', icon: <Lightbulb />, path: '/ai-insights' },
    { text: 'Business Plans', icon: <Assessment />, path: '/business-plans' },
    { text: 'Government Templates', icon: <Description />, path: '/government-templates' },
    { text: 'SBA Lending', icon: <MonetizationOn />, path: '/funding-circle' },
    { text: 'Proposals', icon: <Description />, path: '/proposals' },
    { text: 'Document Templates', icon: <InsertDriveFile />, path: '/document-templates' },
    { text: 'Deal Room Templates', icon: <AutoAwesome />, path: '/templates' },
    { text: 'Integrations', icon: <Email />, path: '/email-calendar' },
    { text: 'Referrals', icon: <PeopleAlt />, path: '/referrals' },
  ];

  const drawer = (
    <Box>
      <Toolbar sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 2 }}>
        <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          Force CRM
        </Typography>
      </Toolbar>
      <Divider />
      <List sx={{ px: 1 }}>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              selected={isActive(item.path)}
              onClick={() => navigate(item.path)}
              sx={{
                borderRadius: 1,
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'white',
                  },
                },
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.text}
                primaryTypographyProps={{
                  fontSize: '0.9rem',
                  fontWeight: isActive(item.path) ? 600 : 400,
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Top AppBar - Only Search, Help, Settings, Profile/Logout */}
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          {/* Search Bar */}
          <TextField
            placeholder="Search..."
            size="small"
            sx={{
              flexGrow: 1,
              maxWidth: 600,
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              borderRadius: 1,
              '& .MuiOutlinedInput-root': {
                color: 'white',
                '& fieldset': {
                  borderColor: 'transparent',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: 'white' }} />
                </InputAdornment>
              ),
            }}
          />


          {/* Business Suite App Switcher */}
          <Box sx={{ ml: 2 }}>
            <BusinessSuiteAppSwitcher position="header" />
          </Box>
          <Box sx={{ flexGrow: 1 }} />

          {/* Help */}
          <IconButton color="inherit" onClick={() => navigate('/help')} title="Help">
            <HelpOutline />
          </IconButton>

          {/* Settings */}
          <IconButton color="inherit" onClick={() => navigate('/settings')} title="Settings">
            <Settings />
          </IconButton>

          {/* Profile Menu */}
          <IconButton onClick={handleProfileMenuOpen} sx={{ ml: 1 }} title="Profile">
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
              {user?.email?.[0]?.toUpperCase() || 'U'}
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
          >
            <MenuItem onClick={() => { navigate('/settings'); handleProfileMenuClose(); }}>
              <ListItemIcon>
                <AccountCircle fontSize="small" />
              </ListItemIcon>
              Profile
            </MenuItem>
            <MenuItem onClick={() => { navigate('/settings'); handleProfileMenuClose(); }}>
              <ListItemIcon>
                <Settings fontSize="small" />
              </ListItemIcon>
              Settings
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Sidebar Drawer */}
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: 8,
        }}
      >
        {children}
      </Box>

      {/* Floating Chat */}
      <FloatingChatBubble />
    </Box>
  );
};

export default SidebarLayout;
